import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, RotateCcw, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import CompleteAndSeeRoom from "@/components/CompleteAndSeeRoom";

export default function HomeStylePiece() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload | analyzing | confirm | result
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile, clear } = useObjectUrl();
  const [item, setItem] = useState(null);
  const [pairings, setPairings] = useState([]);
  const [directions, setDirections] = useState([]);
  const [saved, setSaved] = useState(false);

  const onFile = async (f) => {
    try { const p = await prepareImage(f); setFile(p); setFromFile(p); analyze(p); }
    catch { toast.error(PHOTO_ERROR_MSG); }
  };
  const retake = () => { setFile(null); clear(); setItem(null); setStep("upload"); setSaved(false); };

  const saveToHome = async () => {
    if (!user) { toast("Sign in to save this to My Home."); navigate("/signin"); return; }
    try {
      await api.post("/home/space", {
        kind: "piece", name: `${item.color_name} ${item.item}`,
        style_blend: [item.material, item.style].filter(Boolean).join(" · ") || item.style,
        source: item, item, pairings, directions,
      });
      setSaved(true);
      toast.success("Saved to My Home.");
    } catch { toast.error("Couldn't save that piece."); }
  };

  const analyze = async (f) => {
    setStep("analyzing");
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/home/item/analyze", form);
      if (data.status !== "ok") { toast.error(data.message || "Couldn't read that."); setStep("upload"); return; }
      setItem(data.item); setStep("confirm");
    } catch { toast.error(PHOTO_ERROR_MSG); setStep("upload"); }
  };

  const getPairings = async (it) => {
    setStep("result");
    try {
      const { data } = await api.post("/home/item/pairings", { item: it });
      setPairings(data.pairings); setDirections(data.directions);
    } catch { toast.error("Couldn't build recommendations."); setStep("confirm"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16 min-h-[70vh]">
      {step === "upload" && (
        <div className="text-center max-w-lg mx-auto">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ Home · Style A Piece</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">What are you styling?</h1>
          <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-3">Show MOIRÉ something you already own — a sofa, rug, table, lamp, artwork.</p>
          <div className="mt-8"><UploadZone onFile={onFile} label="Snap your piece" testid="home-piece-upload" preview={preview} onClear={() => { setFile(null); clear(); }} /></div>
        </div>
      )}

      {step === "analyzing" && <AnalyzingSequence stages={["Looking at the piece…", "Reading the colour and material…", "Finding what works with it…"]} />}

      {step === "confirm" && item && (
        <div className="max-w-lg mx-auto text-center">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Is this right?</p>
          <div className="border border-[#F5F2EA]/12 p-6 mt-4 flex items-center gap-4 text-left" data-testid="home-item-confirm">
            <span className="h-14 w-14 rounded-full border border-[#F5F2EA]/15 shrink-0" style={{ backgroundColor: item.color_hex }} />
            <div className="flex-1">
              <div className="font-serif text-2xl text-[#F5F2EA]">{item.color_name} {item.item}</div>
              <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mt-1">{item.material || "—"} · {item.undertone} undertone · {item.style}</div>
            </div>
          </div>
          <div className="flex gap-3 justify-center mt-6">
            <button onClick={() => getPairings(item)} data-testid="home-confirm-yes" className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-7 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Check size={15} /> Find What Works</button>
            <button onClick={retake} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors"><RotateCcw size={14} /> Retake</button>
          </div>
        </div>
      )}

      {step === "result" && item && (
        <div>
          <div className="text-center">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ Home</p>
            <h1 className="font-serif text-3xl md:text-4xl text-[#F5F2EA] mt-1">{item.color_name} {item.item}</h1>
            <p className="font-serif italic text-base text-[#F5F2EA]/70 mt-1">Best pairings for your piece</p>
          </div>

          <div className="divide-y divide-[#F5F2EA]/8 border-y border-[#F5F2EA]/8 mt-6" data-testid="home-pairings">
            {pairings.map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <span className="font-serif text-[#C8A97E] text-sm w-7">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-8 w-8 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: p.hex }} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EA]">{p.name}</div>
                  <div className="font-sans text-[11px] text-[#8A8170]">{p.effect}</div>
                </div>
                <span className="font-serif text-2xl text-[#C8A97E]">{Number(p.score).toFixed(1)}</span>
              </div>
            ))}
          </div>

          {directions.length > 0 && <CompleteAndSeeRoom mode="piece" checkId={null} source={item} directions={directions} />}

          <div className="text-center mt-10 flex flex-wrap items-center justify-center gap-3">
            <button onClick={saveToHome} disabled={saved} data-testid="home-piece-save"
              className="inline-flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-6 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors disabled:opacity-60">
              <Bookmark size={14} strokeWidth={1.6} /> {saved ? "Saved to My Home" : "Save to My Home"}</button>
            <button onClick={retake} className="inline-flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors"><RotateCcw size={13} /> Style Another Piece</button>
          </div>
        </div>
      )}
    </div>
  );
}
